/**
 * API Tokens (PAT) management tab. Tokens authenticate as the current user for
 * programmatic access; the plaintext is shown exactly once, at creation.
 */

import React, { useEffect, useState } from 'react';
import { AlertCircle, Check, Copy, Key, Plus, Trash2 } from 'lucide-react';
import { ApiTokenService, type ApiTokenSummary } from '../../services/apiTokenService';
import { ConfirmModal } from '../../components/ConfirmModal';
import { logger } from '../../utils/logger';
import {
  SCOPE_GROUPS,
  EXPIRY_PRESETS,
  formatDate,
  daysUntil,
  minExpiryDate,
  errorMessage,
} from './ApiTokensTab.constants';

export const ApiTokensTab: React.FC = () => {
  const [tokens, setTokens] = useState<ApiTokenSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  // number = TTL in days, null = never expires
  const [expiry, setExpiry] = useState<number | null>(90);
  const [customMode, setCustomMode] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [creating, setCreating] = useState(false);

  // Plaintext is returned once; hold it in memory only until the user dismisses.
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<ApiTokenSummary | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await ApiTokenService.list();
      setTokens(res.data.tokens);
    } catch (err) {
      logger.error('Failed to load API tokens', { error: err });
      setError(errorMessage(err, 'Failed to load API tokens'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleScope = (value: string) => {
    setScopes((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
    );
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const res = await ApiTokenService.create({ name: name.trim(), scopes, ttlDays: expiry });
      setPlaintext(res.data.token);
      setName('');
      setScopes([]);
      setExpiry(90);
      setCustomMode(false);
      setCustomDate('');
      await load();
    } catch (err) {
      logger.error('Failed to create API token', { error: err });
      setError(errorMessage(err, 'Failed to create API token'));
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy token', { error: err });
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await ApiTokenService.revoke(revokeTarget.id);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Failed to revoke token'));
    } finally {
      setRevokeTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 flex">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <div className="ml-2 text-xs text-red-700 dark:text-red-300">{error}</div>
        </div>
      )}

      {plaintext && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Copy this token now. For your security, it won&apos;t be shown again.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs break-all">
              {plaintext}
            </code>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded transition-colors ${
                copied
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            onClick={() => setPlaintext(null)}
            className="mt-3 text-xs text-amber-700 dark:text-amber-400 underline"
          >
            I&apos;ve saved it
          </button>
        </div>
      )}

      <form
        onSubmit={handleCreate}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
      >
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Create token</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Authenticate scripts and services as your account, without your password.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="What's this token for? e.g. CI pipeline, nightly backup"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Scopes
          </label>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            Grant only the access this token needs. Select at least one.
          </p>
          <div className="space-y-3">
            {Object.entries(SCOPE_GROUPS).map(([group, groupScopes]) => (
              <div key={group}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
                  {group}
                </p>
                <div
                  className={`grid grid-cols-1 gap-2 ${
                    groupScopes.length > 1 ? 'sm:grid-cols-2' : ''
                  }`}
                >
                  {groupScopes.map((s) => {
                    const checked = scopes.includes(s.value);
                    return (
                      <button
                        type="button"
                        key={s.value}
                        onClick={() => toggleScope(s.value)}
                        aria-pressed={checked}
                        className={`flex items-start gap-2.5 text-left p-3 rounded-lg border transition-colors ${
                          checked
                            ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            checked
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-gray-400 dark:border-gray-500'
                          }`}
                        >
                          {checked && <Check className="w-3 h-3" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {s.label}
                          </span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {s.description}
                          </span>
                          <span className="block font-mono text-[11px] text-gray-400 dark:text-gray-500">
                            {s.value}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Expiry
          </label>
          <div className="inline-flex flex-wrap gap-1 rounded-lg border border-gray-200 dark:border-gray-600 p-1">
            {EXPIRY_PRESETS.map((p) => {
              const active = !customMode && expiry === p.days;
              return (
                <button
                  type="button"
                  key={p.label}
                  onClick={() => {
                    setCustomMode(false);
                    setExpiry(p.days);
                  }}
                  aria-pressed={active}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setCustomMode(true);
                setExpiry(daysUntil(customDate));
              }}
              aria-pressed={customMode}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                customMode
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Custom
            </button>
          </div>
          {customMode && (
            <input
              type="date"
              min={minExpiryDate()}
              value={customDate}
              onChange={(e) => {
                setCustomDate(e.target.value);
                setExpiry(daysUntil(e.target.value));
              }}
              className="mt-2 block w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            {customMode && expiry === null
              ? 'Pick a future date for the token to expire.'
              : expiry === null
                ? 'Token will not expire — revoke it manually when no longer needed.'
                : `Expires in ${expiry} day${expiry === 1 ? '' : 's'}${expiry > 365 ? ' (capped at 365).' : '.'}`}
          </p>
        </div>
        <button
          type="submit"
          disabled={
            creating || !name.trim() || scopes.length === 0 || (customMode && expiry === null)
          }
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          {creating ? 'Creating…' : 'Create token'}
        </button>
      </form>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Your tokens ({tokens.length})
          </h3>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
            No API tokens yet. Create one above to access the API programmatically.
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {tokens.map((token) => {
              const expired = !!token.expiresAt && new Date(token.expiresAt).getTime() < Date.now();
              const inactive = token.revoked || expired;
              return (
                <div
                  key={token.id}
                  className={`px-4 py-3 flex items-center justify-between gap-3 ${
                    inactive ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Key className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                        <span className="truncate">{token.name}</span>
                        <span className="font-mono text-xs text-gray-400">…{token.lastEight}</span>
                        {token.revoked ? (
                          <span className="rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-[11px] font-medium text-red-600 dark:text-red-400">
                            revoked
                          </span>
                        ) : expired ? (
                          <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                            expired
                          </span>
                        ) : (
                          <span className="rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-[11px] font-medium text-green-600 dark:text-green-400">
                            active
                          </span>
                        )}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {token.scopes.length ? (
                          token.scopes.map((s) => (
                            <span
                              key={s}
                              className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-300"
                            >
                              {s}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            full access
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {token.expiresAt ? `expires ${formatDate(token.expiresAt)}` : 'no expiry'} ·
                        last used {formatDate(token.lastUsedAt)}
                      </p>
                    </div>
                  </div>
                  {!token.revoked && (
                    <button
                      onClick={() => setRevokeTarget(token)}
                      title="Revoke token"
                      className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
        title="Revoke token?"
        message={`Revoke "${revokeTarget?.name}"? Any client using it will immediately lose access. This cannot be undone.`}
        confirmText="Revoke"
        variant="danger"
      />
    </div>
  );
};
